/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import cx from 'classnames';
import { memo, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { Typography } from '@mui/material';
import ButtonBase from '@mui/material/ButtonBase';
import Collapse from '@mui/material/Collapse';
import { UiSchema } from '@rjsf/utils';

import { Namespaces } from '../../../../../../i18n';
import { useCustomCollapsibleFieldStyles } from './CustomCollapsibleField.styles';

type Props = {
  name: string;
  errorSchema?: Record<string, unknown>;
  schema: {
    title?: string;
  };
  registry: {
    fields: Record<string, any>;
  };
  uiSchema?: UiSchema;
  idSchema: {
    $id: string;
  };
};

export const CustomCollapsibleField = memo<Props>(props => {
  const classes = useCustomCollapsibleFieldStyles();
  const { t } = useTranslation(Namespaces.Cluster);

  const {
    schema: { title },
    uiSchema,
    registry: { fields },
    name,
    errorSchema,
  } = props;

  const [collapsed, setCollapsed] = useState(uiSchema?.collapse.collapsed ?? true);

  const {
    collapse: { field },
  } = uiSchema as UiSchema & { collapse: { field: string } };

  const CustomCollapseElement = fields[field];

  const collapseTitle = (uiSchema?.['ui:title'] as string) || title || name;
  const customizedId = collapsed ? props.idSchema.$id : undefined;

  const handleCollapsed = () => {
    setCollapsed(!collapsed);
  };
  const hasActualValue = useMemo(
    () =>
      !!Object.keys(uiSchema ?? {}).find(i => {
        const options = (uiSchema ?? {})[i]?.['ui:options'];
        return typeof options === 'object' && options && Object.hasOwn(options, 'actualValue');
      }),
    [uiSchema],
  );

  const hasError = useMemo(() => !!Object.keys(errorSchema ?? {}).length, [errorSchema]);

  return (
    <div id={customizedId}>
      <ButtonBase
        onClick={handleCollapsed}
        className={cx(classes.button, {
          [classes.hasError]: hasError,
          [classes.hasWarning]: hasActualValue && !hasError,
        })}
      >
        <Typography variant="h3" component="h3" className={classes.title}>
          {t(`details.parameters.collapse_titles.${name}`, collapseTitle)}
        </Typography>
        {!collapsed ? <ExpandLess /> : <ExpandMore />}
      </ButtonBase>
      <Collapse in={!collapsed} timeout="auto" unmountOnExit>
        {!collapsed && <CustomCollapseElement {...props} />}
      </Collapse>
    </div>
  );
});
